"use client";

import FreeVhfProgressInModule from "@/components/FreeVhfProgressInModule";
import FreeVhfScenarioBadge from "@/components/FreeVhfScenarioBadge";
import FreeVhfYouTubePlayer from "@/components/FreeVhfYouTubePlayer";
import FreeVhfPhraseSelfCheck from "@/components/FreeVhfPhraseSelfCheck";
import FreeVhfLessonGate, { type QuestionDef } from "@/components/FreeVhfLessonGate";
import FreeVhfKeyPhrasesCollapsible from "@/components/FreeVhfKeyPhrasesCollapsible";
import FreeVhfReadTranscript from "@/components/FreeVhfReadTranscript";
import FreeVhfLeaveConfirm from "@/components/FreeVhfLeaveConfirm";
import { DEFAULT_UI } from "@/lib/ui-defaults";
import { type UIStrings } from "@/lib/schemas";
import { type CourseProgress, type FreeVhfScenario } from "@/lib/schemas";
import { getLocalizedPath, getFreeVhfLessonPath } from "@/lib/routing";
import { useParams } from "next/navigation";
import { type Locale } from "@/lib/i18n-config";
import { useMemo } from "react";

interface FreeVhfLessonM12Props {
  moduleNum: 1 | 2;
  lessonTitle: string;
  currentIndex: number;
  totalCalls: number;
  courseProgress: CourseProgress;
  whyText: string | null;
  tipText: string | null;
  videoId: string;
  backHref: string;
  scenarioId: string;
  nextLessonHref?: string;
  endModuleHref?: string;
  nextLessonId?: string;
  hasNext: boolean;
  phraseCheck: FreeVhfScenario["phrase_self_check"] | null;
  phraseCheckQuestion: string | null;
  phraseCheckOptions: { id: string; label: string }[];
  keyPhrases: string[];
  hasTranscript: boolean;
  transcriptText: string | null;
  ui: UIStrings;
}

export default function FreeVhfLessonM12({
  moduleNum,
  lessonTitle,
  currentIndex,
  totalCalls,
  courseProgress,
  whyText,
  tipText,
  videoId,
  backHref,
  scenarioId,
  nextLessonHref: _nextLessonHref,
  endModuleHref: _endModuleHref,
  nextLessonId,
  hasNext,
  phraseCheck,
  phraseCheckQuestion,
  phraseCheckOptions,
  keyPhrases,
  hasTranscript,
  transcriptText,
  ui,
}: FreeVhfLessonM12Props) {
  void _nextLessonHref;
  void _endModuleHref;
  const sr = ui.freeVhf?.selfReport;
  const fbSr = DEFAULT_UI.freeVhf?.selfReport;
  const fbPhrase = DEFAULT_UI.freeVhf?.phraseCheck?.prompt;
  const params = useParams();
  const locale = (params.locale as Locale) || "en";
  
  const questions = useMemo(() => {
    const m1: QuestionDef[] = [
      {
        id: "understand",
        question: sr?.m1UnderstandQuestion ?? fbSr?.m1UnderstandQuestion ?? "",
        options: [
          { value: "almost-everything", label: sr?.m1UnderstandOpt1 ?? fbSr?.m1UnderstandOpt1 ?? "" },
          { value: "some-parts", label: sr?.m1UnderstandOpt2 ?? fbSr?.m1UnderstandOpt2 ?? "" },
          { value: "very-little", label: sr?.m1UnderstandOpt3 ?? fbSr?.m1UnderstandOpt3 ?? "" },
        ],
      },
      {
        id: "comfortable",
        question: sr?.m1ComfortableQuestion ?? fbSr?.m1ComfortableQuestion ?? "",
        options: [
          { value: "yes", label: sr?.m1ComfortableYes ?? fbSr?.m1ComfortableYes ?? "" },
          { value: "no", label: sr?.m1ComfortableNo ?? fbSr?.m1ComfortableNo ?? "" },
        ],
      },
    ];

    const m2: QuestionDef[] = [
      {
        id: "who",
        question: sr?.m2WhoQuestion ?? fbSr?.m2WhoQuestion ?? "",
        options: [
          { value: "vessel", label: sr?.m2WhoVessel ?? fbSr?.m2WhoVessel ?? "" },
          { value: "shore", label: sr?.m2WhoShore ?? fbSr?.m2WhoShore ?? "" },
          { value: "not-sure", label: sr?.m2WhoNotSure ?? fbSr?.m2WhoNotSure ?? "" },
        ],
        multiple: true,
      },
      {
        id: "clear",
        question: sr?.m2ClearQuestion ?? fbSr?.m2ClearQuestion ?? "",
        options: [
          { value: "very-clear", label: sr?.m2ClearVery ?? fbSr?.m2ClearVery ?? "" },
          { value: "partly-clear", label: sr?.m2ClearPartly ?? fbSr?.m2ClearPartly ?? "" },
          { value: "not-clear", label: sr?.m2ClearNot ?? fbSr?.m2ClearNot ?? "" },
        ],
      },
    ];

    return moduleNum === 1 ? m1 : m2;
  }, [moduleNum, sr, fbSr]);

  return (
    <div className="max-w-3xl mx-auto py-10 sm:py-12 lg:py-14 page-gutter">
      <FreeVhfProgressInModule
        current={currentIndex}
        total={totalCalls}
        courseProgress={courseProgress}
        className="mb-6"
      />
      <h1 className="text-2xl sm:text-3xl font-bold text-content mb-6">{lessonTitle}</h1>
      {whyText && <p className="content-prose text-muted text-sm mb-4">{whyText}</p>}
      <div className="mb-8">
        <FreeVhfScenarioBadge label={lessonTitle} className="mb-2 block" />
        <div className="free-vhf-lesson-lcp-player rounded-lg overflow-hidden">
          <FreeVhfYouTubePlayer videoId={videoId} title={lessonTitle} backHref={backHref} className="w-full h-full" />
        </div>
      </div>
      <div className="space-y-8 mb-8">
        {phraseCheck && phraseCheckOptions.length > 0 && (
          <FreeVhfPhraseSelfCheck
            question={phraseCheckQuestion ?? ui.freeVhf?.phraseCheck?.prompt ?? fbPhrase ?? ""}
            options={phraseCheckOptions}
            idPrefix={`m${moduleNum}-${scenarioId}-phrase-check`}
            multiple
            selectAllHint={ui.freeVhf?.phraseCheck?.selectAllHint}
          />
        )}
        <FreeVhfLessonGate
          key={scenarioId}
          scenarioId={scenarioId}
          module={moduleNum}
          questions={questions}
          nextLessonHref={nextLessonId ? getFreeVhfLessonPath(nextLessonId, locale) : undefined}
          endModuleHref={nextLessonId ? undefined : getLocalizedPath(`/free-vhf/module/${moduleNum}/end`, locale)}
          nextLessonId={nextLessonId}
          hasNext={hasNext}
        >
          {tipText && <p className="text-sm text-muted border-l-4 border-primary-200 pl-3 py-1">{tipText}</p>}
          {keyPhrases.length > 0 && <FreeVhfKeyPhrasesCollapsible phrases={keyPhrases} id={`m${moduleNum}-${scenarioId}-key-phrases`} />}
          {hasTranscript && transcriptText && <FreeVhfReadTranscript transcriptText={transcriptText} id={`m${moduleNum}-${scenarioId}-transcript`} />}
        </FreeVhfLessonGate>
      </div>
      <FreeVhfLeaveConfirm leaveConfirmMessage={ui.freeVhf?.completion?.leaveConfirmMessage} />
    </div>
  );
}
